import React, { useCallback, useContext, useEffect, useRef, useState, ButtonHTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

import PulseLoader from 'react-spinners/PulseLoader';
import BounceLoader from 'react-spinners/BounceLoader';

import { ThemeContext } from '@dstantwo/ullr-react-components';

// Type for the theme context colors
interface ThemeColors {
  TRANSPARENT: string;
  PRIMARY: string;
  OFF_WHITE: string;
  DARK: string;
  DARK_GRAY: string;
  RED_ALERT: string;
  [key: string]: string;
}

interface ThemeContextValue {
  COLORS: ThemeColors;
}

// Props for styled button components
interface StyledButtonProps {
  inverse?: boolean;
  danger?: boolean;
  dangerInverse?: boolean;
  backgroundColor?: string;
  disabled?: boolean;
}

export const UllrClickableElement = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  border: 0;
  outline: 0;
  border-radius: 4px;

  background-color: ${() => (useContext(ThemeContext) as ThemeContextValue).COLORS.TRANSPARENT};

  -webkit-transition: all 150ms ease;
  transition: all 150ms ease;

  cursor: pointer;

  &:hover:enabled {
    transform: translateY(-1px);
    box-shadow: 0 7px 14px rgba(50, 50, 93, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08);
  }

  &:active:enabled:before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 4px;
    background-image: linear-gradient(rgb(0 0 0/40%) 0 0);
  }
`;

export const UllrClickableText = styled(UllrClickableElement)`
  justify-content: inherit;
  width: fit-content;
  color: ${() => (useContext(ThemeContext) as ThemeContextValue).COLORS.PRIMARY};
  font-weight: 600;
`;

const UllrButtonStyledComponent = styled(UllrClickableElement)<StyledButtonProps>`
  white-space: nowrap;
  height: 40px;
  line-height: 40px;
  padding: 0 14px;
  box-shadow: 0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08);
  color: ${(props) => {
    const { COLORS } = useContext(ThemeContext) as ThemeContextValue;
    if (props.inverse) {
      return props.disabled ? COLORS.DARK : COLORS.PRIMARY;
    }
    if (props.dangerInverse) {
      return COLORS.RED_ALERT;
    }
    return COLORS.OFF_WHITE;
  }};

  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  background-color: ${(props) => {
    const { COLORS } = useContext(ThemeContext) as ThemeContextValue;
    if (props.backgroundColor) {
      return props.backgroundColor;
    }
    if (props.inverse) {
      return COLORS.OFF_WHITE;
    }
    if (props.danger) {
      return COLORS.RED_ALERT;
    }
    if (props.dangerInverse) {
      return COLORS.OFF_WHITE;
    }
    return COLORS.PRIMARY;
  }};
  text-decoration: none;

  margin-top: 10px;

  &:disabled {
    background-color: ${() => (useContext(ThemeContext) as ThemeContextValue).COLORS.DARK_GRAY};
  }

  svg {
    margin-left: 8px;
  }
`;

export const UllrButtonCircleStyledComponent = styled(UllrButtonStyledComponent)`
  height: 50px;
  width: 50px;
  border-radius: 25px;
  margin-top: 0px;

  svg {
    margin-left: 0px;
  }
`;

// Props for UllrButton component
interface UllrButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, StyledButtonProps {
  children?: ReactNode;
  isLoading?: boolean;
}

export function UllrButton({ children, isLoading, ...props }: UllrButtonProps) {
  const { COLORS } = useContext(ThemeContext) as ThemeContextValue;
  /* Capture the dimensions of the button before the loading happens
  so it doesn't change size when showing the loader */
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const ref = useRef<HTMLButtonElement>(null);

  // Capture natural dimensions after paint so the measurement reflects
  // fully-loaded fonts and CSS.  Re-runs when children change.
  useEffect(
    () => {
      if (ref.current && ref.current.getBoundingClientRect().width) {
        setWidth(ref.current.getBoundingClientRect().width);
      }
      if (ref.current && ref.current.getBoundingClientRect().height) {
        setHeight(ref.current.getBoundingClientRect().height);
      }
    },
    [children]
  );

  // Only lock dimensions while loading so the spinner doesn't resize the
  // button.  During normal display the button sizes naturally via CSS,
  // which avoids clipping when children text changes.
  const widthHeightStyle =
    isLoading && width && height
      ? {
          width: `${width}px`,
          height: `${height}px`,
        }
      : {};
  return (
    <UllrButtonStyledComponent
      ref={ref}
      style={{
        ...widthHeightStyle,
        pointerEvents: isLoading ? 'none' : 'auto',
        ...props.style,
      }}
      {...props}
    >
      {!isLoading ? (
        children
      ) : (
        <PulseLoader color={props.inverse ? COLORS.PRIMARY : COLORS.OFF_WHITE} loading={isLoading} size={10} margin={2} />
      )}
    </UllrButtonStyledComponent>
  );
}

// Props for UllrButtonCircle component
interface UllrButtonCircleProps extends ButtonHTMLAttributes<HTMLButtonElement>, StyledButtonProps {
  children?: ReactNode;
  isLoading?: boolean;
}

export const UllrButtonCircle = React.forwardRef<HTMLButtonElement, UllrButtonCircleProps>(
  function UllrButtonCircle({ children, isLoading, ...props }, forwardedRef) {
    const { COLORS } = useContext(ThemeContext) as ThemeContextValue;
    /* Capture the dimensions of the button before the loading happens
    so it doesn't change size when showing the loader */
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const ref = useRef<HTMLButtonElement>(null);

    // Merge the internal measurement ref with any forwarded ref (e.g. a Radix
    // DropdownMenu trigger that anchors a menu to this button).
    const setRef = useCallback((node: HTMLButtonElement | null) => {
      ref.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    }, [forwardedRef]);

    // Capture natural dimensions after paint so the measurement reflects
    // fully-loaded fonts and CSS.  Re-runs when children change.
    useEffect(
      () => {
        if (ref.current && ref.current.getBoundingClientRect().width) {
          setWidth(ref.current.getBoundingClientRect().width);
        }
        if (ref.current && ref.current.getBoundingClientRect().height) {
          setHeight(ref.current.getBoundingClientRect().height);
        }
      },
      [children]
    );

    // Only lock dimensions while loading so the spinner doesn't resize the
    // button.  During normal display the button sizes naturally via CSS.
    const widthHeightStyle =
      isLoading && width && height
        ? {
            width: `${width}px`,
            height: `${height}px`,
          }
        : {};
    return (
      <UllrButtonCircleStyledComponent
        ref={setRef}
        style={{
          ...widthHeightStyle,
          pointerEvents: isLoading ? 'none' : 'auto',
          ...props.style,
        }}
        {...props}
      >
        {!isLoading ? (
          children
        ) : (
          <BounceLoader color={props.inverse ? COLORS.PRIMARY : COLORS.OFF_WHITE} loading={isLoading} size={21} />
        )}
      </UllrButtonCircleStyledComponent>
    );
  }
);
UllrButtonCircle.displayName = 'UllrButtonCircle';
